/**
 * Linked tasks and habits (ADR 0024): a task or habit kept in several workspaces. The app only says where it
 * should be (`link.workspaces` on a task, `linkWorkspaces` on a habit); the server makes and updates the copies
 * (`server/drang/links.py`).
 */
import { LoroMap, type LoroDoc } from "loro-crdt";
import { commitMessage } from "./actor";
import { C } from "./schema";
import { createPropDef, setProp } from "./ops";

export const LINK_WS = "link.workspaces";

export function parseWs(value: unknown): string[] {
  return [
    ...new Set(
      String(value ?? "")
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean),
    ),
  ].sort();
}

export function fmtWs(ids: Iterable<string>): string {
  return [...new Set(ids)].sort().join(",");
}

/** The hidden bookkeeping properties (ADR 0013), made where they are missing. */
function ensureDefs(doc: LoroDoc): void {
  const defs = doc.getMap(C.propDefs);
  for (const [id, name] of [
    ["link.id", "Link id"],
    [LINK_WS, "Linked workspaces"],
    ["link.from", "Linked from"],
    ["link.known", "Linked before"],
  ] as const) {
    if (defs.get(id) === undefined)
      createPropDef(doc, {
        id,
        name,
        kind: "text",
        spec: { hidden: true },
        level: "power",
        shownInQuickAdd: false,
      } as never);
  }
}

/** Where a task should be: this workspace and `others`. */
export function setTaskWorkspaces(doc: LoroDoc, taskId: string, here: string, others: string[]): void {
  ensureDefs(doc);
  setProp(doc, taskId, LINK_WS, fmtWs([here, ...others]));
}

/** Where a habit should be: this workspace and `others`. */
export function setCounterWorkspaces(doc: LoroDoc, counterId: string, here: string, others: string[]): void {
  const m = doc.getMap(C.counters).get(counterId) as LoroMap | undefined;
  if (!m) throw new Error("counters.noSuchCounter");
  m.set("linkWorkspaces", fmtWs([here, ...others]));
  doc.commit({ message: commitMessage({ op: "linkCounter" }) });
}
